import React, { useState, useEffect, useRef } from 'react';
import { PaperclipIcon } from './icons';
import './AttachmentGrid.css';

const isImage = (ct) => typeof ct === 'string' && ct.startsWith('image/');
const isVideo = (ct) => typeof ct === 'string' && ct.startsWith('video/');
const isMedia = (ct) => isImage(ct) || isVideo(ct);

function formatSize(bytes) {
  if (bytes == null) return '';
  const b = Number(bytes);
  if (Number.isNaN(b)) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Renders a set of attachments as tiles. Images and videos (e.g. screen
 * recordings) are fetched as authed blobs and previewed/played inline; other
 * files show a download chip. Used by the Attachments panel and inside comments.
 */
function AttachmentGrid({ caseId, attachments = [], fetchBlob, compact = false, onDelete }) {
  const [mediaUrls, setMediaUrls] = useState({});
  const [error, setError] = useState(null);
  const urlsRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        attachments.filter((a) => isMedia(a.content_type)).map(async (a) => {
          try {
            const res = await fetchBlob(caseId, a.id);
            if (!cancelled) next[a.id] = URL.createObjectURL(res.data);
          } catch (e) { /* skip */ }
        })
      );
      if (cancelled) {
        Object.values(next).forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } });
        return;
      }
      Object.values(urlsRef.current).forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } });
      urlsRef.current = next;
      setMediaUrls(next);
    })();
    return () => { cancelled = true; };
  }, [attachments, caseId, fetchBlob]);

  useEffect(() => () => {
    Object.values(urlsRef.current).forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } });
  }, []);

  const handleDownload = async (a) => {
    try {
      const res = await fetchBlob(caseId, a.id);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.original_file_name || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Download failed');
    }
  };

  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      {error && <div className="attachment-grid-error" role="alert">{error}</div>}
      <div className={`attachment-grid${compact ? ' compact' : ''}`}>
        {attachments.map((a) => {
          const video = isVideo(a.content_type);
          const url = mediaUrls[a.id];
          return (
            <div key={a.id} className={`attachment-card${video ? ' is-video' : ''}`}>
              {onDelete && (
                <button
                  type="button"
                  className="attachment-delete"
                  title="Delete file"
                  onClick={() => { if (window.confirm(`Delete "${a.original_file_name}"?`)) onDelete(a.id); }}
                >
                  ×
                </button>
              )}
              {video && url ? (
                <video className="attachment-video" src={url} controls preload="metadata" />
              ) : isImage(a.content_type) && url ? (
                <a href={url} target="_blank" rel="noreferrer" className="attachment-thumb">
                  <img src={url} alt={a.original_file_name} />
                </a>
              ) : (
                <div className="attachment-file-icon" aria-hidden><PaperclipIcon size={26} /></div>
              )}
              <div className="attachment-meta">
                <button type="button" className="attachment-name" onClick={() => handleDownload(a)} title="Download">
                  {a.original_file_name}
                </button>
                <span className="attachment-sub">
                  {formatSize(a.file_size_bytes)}
                  {a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ''}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default AttachmentGrid;
